import {
  AdminPanelSettings as AdminIcon,
  Assignment as AssignmentIcon,
  PhoneMissed as ContactAttemptIcon,
  Block as BlockIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  ContentCut as CutIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Error as ErrorIcon,
  Event as EventIcon,
  FactCheck as FactCheckIcon,
  MeetingRoom as MeetingRoomIcon,
  NotificationsActive as NotificationIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Sms as SmsIcon,
  VideoCall as VideoCallIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { WorkItemTypeEnum } from "../store/services/timelineApiRTK";

/**
 * Gets an appropriate icon for the given work item type
 */
export const getWorkItemIcon = (type: WorkItemTypeEnum | null) => {
  if (!type) return <EventIcon fontSize="small" />;

  switch (type) {
    case WorkItemTypeEnum.Email:
      return <EmailIcon fontSize="small" />;
    case WorkItemTypeEnum.PhoneCall:
      return <PhoneIcon fontSize="small" />;
    case WorkItemTypeEnum.PhysicalMeeting:
      return <PersonIcon fontSize="small" />;
    case WorkItemTypeEnum.OpportunityCall:
      return <BusinessIcon fontSize="small" />;
    case WorkItemTypeEnum.Sms:
      return <SmsIcon fontSize="small" />;
    case WorkItemTypeEnum.WelcomeCall:
      return <PhoneIcon fontSize="small" />;
    case WorkItemTypeEnum.ReconnectToCustomer:
      return <NotificationIcon fontSize="small" />;
    case WorkItemTypeEnum.HandleEmail:
      return <EmailIcon fontSize="small" />;
    case WorkItemTypeEnum.FullmaktRejected:
      return <BlockIcon fontSize="small" />;
    case WorkItemTypeEnum.ContractProductionFacility:
      return <FactCheckIcon fontSize="small" />;
    case WorkItemTypeEnum.Z04:
      return <DescriptionIcon fontSize="small" />;
    case WorkItemTypeEnum.CreateContractFailed:
      return <ErrorIcon fontSize="small" />;
    case WorkItemTypeEnum.AvtalsBrott:
      return <CutIcon fontSize="small" />;
    case WorkItemTypeEnum.MessageExternalSale:
      return <AssignmentIcon fontSize="small" />;
    case WorkItemTypeEnum.ManualContractCancellation:
      return <CutIcon fontSize="small" />;
    case WorkItemTypeEnum.NegativAperak:
      return <WarningIcon fontSize="small" />;
    case WorkItemTypeEnum.KanEjSkickaZ03:
      return <BlockIcon fontSize="small" />;
    case WorkItemTypeEnum.EmailError:
      return <ErrorIcon fontSize="small" />;
    case WorkItemTypeEnum.MissingPoaFile:
      return <WarningIcon fontSize="small" />;
    case WorkItemTypeEnum.Z05LK:
      return <DescriptionIcon fontSize="small" />;
    case WorkItemTypeEnum.DigitalMeeting:
      return <VideoCallIcon fontSize="small" />;
    case WorkItemTypeEnum.Admin:
      return <AdminIcon fontSize="small" />;
    case WorkItemTypeEnum.ContactAttempt:
      return <ContactAttemptIcon fontSize="small" />;
    // Follow-up activities
    case WorkItemTypeEnum.FollowUpEmail:
      return <EmailIcon fontSize="small" />;
    case WorkItemTypeEnum.FollowUpPhoneCall:
      return <PhoneIcon fontSize="small" />;
    case WorkItemTypeEnum.FollowUpSms:
      return <SmsIcon fontSize="small" />;
    case WorkItemTypeEnum.FollowUpDigitalMeeting:
      return <VideoCallIcon fontSize="small" />;
    case WorkItemTypeEnum.FollowUpPhysicalMeeting:
      return <MeetingRoomIcon fontSize="small" />;
    default:
      return <EventIcon fontSize="small" />;
  }
};

/**
 * Gets a human-readable name for the given work item type
 */
export const getWorkItemTypeName = (type?: WorkItemTypeEnum | null): string => {
  if (!type) return "Aktivitet";

  switch (type) {
    case WorkItemTypeEnum.Email:
      return "E-post";
    case WorkItemTypeEnum.PhoneCall:
      return "Telefonsamtal";
    case WorkItemTypeEnum.PhysicalMeeting:
      return "Möte";
    case WorkItemTypeEnum.OpportunityCall:
      return "Uppföljning";
    case WorkItemTypeEnum.Sms:
      return "SMS";
    case WorkItemTypeEnum.WelcomeCall:
      return "Välkomstsamtal";
    case WorkItemTypeEnum.ReconnectToCustomer:
      return "Återkoppling";
    case WorkItemTypeEnum.HandleEmail:
      return "Hantera e-post";
    case WorkItemTypeEnum.FullmaktRejected:
      return "Fullmakt avvisad";
    case WorkItemTypeEnum.ContractProductionFacility:
      return "Kontraktsproduktionsanläggning";
    case WorkItemTypeEnum.Z04:
      return "Z04";
    case WorkItemTypeEnum.CreateContractFailed:
      return "Kontraktskapande misslyckades";
    case WorkItemTypeEnum.AvtalsBrott:
      return "Avtalsbrott";
    case WorkItemTypeEnum.MessageExternalSale:
      return "Meddelande extern försäljning";
    case WorkItemTypeEnum.ManualContractCancellation:
      return "Manuell kontraktsannullering";
    case WorkItemTypeEnum.NegativAperak:
      return "Negativ Aperak";
    case WorkItemTypeEnum.KanEjSkickaZ03:
      return "Kan ej skicka Z03";
    case WorkItemTypeEnum.EmailError:
      return "E-postfel";
    case WorkItemTypeEnum.MissingPoaFile:
      return "Saknad POA-fil";
    case WorkItemTypeEnum.Z05LK:
      return "Z05LK";
    case WorkItemTypeEnum.DigitalMeeting:
      return "Digitalt möte";
    case WorkItemTypeEnum.Admin:
      return "Administration";
    case WorkItemTypeEnum.ContactAttempt:
      return "Kontaktförsök";
    // Follow-up activities
    case WorkItemTypeEnum.FollowUpEmail:
      return "Uppföljning via e-post";
    case WorkItemTypeEnum.FollowUpPhoneCall:
      return "Uppföljning via telefon";
    case WorkItemTypeEnum.FollowUpSms:
      return "Uppföljning via SMS";
    case WorkItemTypeEnum.FollowUpDigitalMeeting:
      return "Uppföljning via digitalt möte";
    case WorkItemTypeEnum.FollowUpPhysicalMeeting:
      return "Uppföljning via fysiskt möte";
    default:
      return "Aktivitet";
  }
};

/**
 * Gets the CSS color for a work item type
 */
export const getWorkItemColor = (type?: WorkItemTypeEnum | null): string => {
  if (!type) return "#757575"; // Gray

  switch (type) {
    case WorkItemTypeEnum.Email:
    case WorkItemTypeEnum.HandleEmail:
    case WorkItemTypeEnum.FollowUpEmail:
      return "#2196f3"; // Blue

    case WorkItemTypeEnum.PhoneCall:
    case WorkItemTypeEnum.WelcomeCall:
    case WorkItemTypeEnum.FollowUpPhoneCall:
      return "#ff9800"; // Orange

    case WorkItemTypeEnum.PhysicalMeeting:
    case WorkItemTypeEnum.FollowUpPhysicalMeeting:
      return "#4caf50"; // Green

    case WorkItemTypeEnum.OpportunityCall:
      return "#9c27b0"; // Purple

    case WorkItemTypeEnum.Sms:
    case WorkItemTypeEnum.FollowUpSms:
      return "#00bcd4"; // Cyan

    case WorkItemTypeEnum.ReconnectToCustomer:
      return "#f44336"; // Red

    case WorkItemTypeEnum.DigitalMeeting:
    case WorkItemTypeEnum.FollowUpDigitalMeeting:
      return "#3f51b5"; // Indigo

    case WorkItemTypeEnum.Admin:
      return "#6B655F"; // Slate grey – intern admin

    case WorkItemTypeEnum.ContactAttempt:
      return "#9B6B00"; // Amber – effort/kontaktförsök

    case WorkItemTypeEnum.FullmaktRejected:
    case WorkItemTypeEnum.CreateContractFailed:
    case WorkItemTypeEnum.EmailError:
    case WorkItemTypeEnum.KanEjSkickaZ03:
      return "#e91e63"; // Pink (for errors)

    case WorkItemTypeEnum.AvtalsBrott:
    case WorkItemTypeEnum.ManualContractCancellation:
      return "#ff5722"; // Deep Orange (for cancellations)

    case WorkItemTypeEnum.NegativAperak:
    case WorkItemTypeEnum.MissingPoaFile:
      return "#ffc107"; // Amber (for warnings)

    case WorkItemTypeEnum.Z04:
    case WorkItemTypeEnum.Z05LK:
    case WorkItemTypeEnum.ContractProductionFacility:
    case WorkItemTypeEnum.MessageExternalSale:
      return "#607d8b"; // Blue Gray (for documents/messages)

    default:
      return "#757575"; // Gray
  }
};

/**
 * Determine if a work item requires immediate attention
 */
export const isUrgentWorkItem = (type?: WorkItemTypeEnum | null): boolean => {
  if (!type) return false;

  const urgent = [
    WorkItemTypeEnum.CreateContractFailed,
    WorkItemTypeEnum.EmailError,
    WorkItemTypeEnum.FullmaktRejected,
    WorkItemTypeEnum.AvtalsBrott,
    WorkItemTypeEnum.NegativAperak,
    WorkItemTypeEnum.KanEjSkickaZ03,
    WorkItemTypeEnum.MissingPoaFile,
  ];

  return urgent.includes(type);
};

export const isMeetingType = (type?: WorkItemTypeEnum | null): boolean =>
  type === WorkItemTypeEnum.FollowUpPhysicalMeeting ||
  type === WorkItemTypeEnum.FollowUpDigitalMeeting;

// Define work item categories
export enum WorkItemCategory {
  COMMUNICATION = "Planering",
  FOLLOWUP = "Mötesbokning",
}

export const WORK_ITEM_CATEGORIES: Record<
  WorkItemCategory,
  WorkItemTypeEnum[]
> = {
  // Planera = interna intentioner. Möten exkluderas eftersom ett möte
  // per definition kräver en överenskommelse med kunden — det hör hemma
  // under Mötesbokning (FOLLOWUP).
  [WorkItemCategory.COMMUNICATION]: [
    WorkItemTypeEnum.Email,
    WorkItemTypeEnum.PhoneCall,
    WorkItemTypeEnum.Sms,
    WorkItemTypeEnum.Admin,
  ],
  [WorkItemCategory.FOLLOWUP]: [
    WorkItemTypeEnum.FollowUpEmail,
    WorkItemTypeEnum.FollowUpPhoneCall,
    WorkItemTypeEnum.FollowUpSms,
    WorkItemTypeEnum.FollowUpDigitalMeeting,
    WorkItemTypeEnum.FollowUpPhysicalMeeting,
  ],
};

/**
 * Get color for a specific category
 */
export const getCategoryColor = (category: WorkItemCategory): string => {
  switch (category) {
    case WorkItemCategory.COMMUNICATION:
      return "#2196f3"; // Blue
    case WorkItemCategory.FOLLOWUP:
      return "#9c27b0"; // Purple
    default:
      return "#757575"; // Gray
  }
};

// Get category icon
export const getCategoryIcon = (category: WorkItemCategory) => {
  switch (category) {
    case WorkItemCategory.COMMUNICATION:
      return <EditIcon fontSize="small" />;
    case WorkItemCategory.FOLLOWUP:
      return <MeetingRoomIcon fontSize="small" />;
    default:
      return <CategoryIcon fontSize="small" />;
  }
};

// Få team-färg baserat på teamID
export const getTeamColor = (teamId: number | undefined): string => {
  const colors: Record<number, string> = {
    1: "#3f51b5", // Kundservice
    2: "#f44336", // Företagssälj
    3: "#4caf50", // Bredband
    7: "#ff9800", // Fakturering
    18: "#2196f3", // Privatsälj
    24: "#9c27b0", // Kundutredningar
    28: "#9c27b0", // Privatsälj Ny kund
  };
  if (!teamId) return "#607d8b";
  return colors[teamId] || "#607d8b";
};

// Define constants for completion results
export const ACTION_RESULTS = {
  COMPLETED: "Completed",
  SKIPPED: "Skipped",
  CUSTOMER_UNAVAILABLE: "CustomerUnavailable",
  CUSTOMER_NOT_INTERESTED: "CustomerNotInterested",
  POSTPONED: "Postponed",
  NO_ANSWER: "NoAnswer",
};

// Human-readable labels for the results
export const ACTION_RESULT_LABELS = {
  [ACTION_RESULTS.COMPLETED]: "Genomförd",
  [ACTION_RESULTS.SKIPPED]: "Skippad",
  [ACTION_RESULTS.CUSTOMER_UNAVAILABLE]: "Kund inte tillgänglig",
  [ACTION_RESULTS.CUSTOMER_NOT_INTERESTED]: "Kund inte intresserad",
  [ACTION_RESULTS.POSTPONED]: "Uppskjuten",
  [ACTION_RESULTS.NO_ANSWER]: "Inget svar",
};

export const formatLastActionResult = (
  actionText: string | null | undefined
): string | null => {
  if (!actionText) {
    return null; // Return null if no text
  }

  try {
    const parsed = JSON.parse(actionText);
    // Check if it's an object and has the 'Result' property which is a string
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "Result" in parsed &&
      typeof parsed.Result === "string"
    ) {
      // Return the human-readable label if found, otherwise the raw result string
      // Explicitly cast parsed.Result as keyof typeof ACTION_RESULT_LABELS
      const resultKey = parsed.Result as keyof typeof ACTION_RESULT_LABELS;
      return ACTION_RESULT_LABELS[resultKey] || parsed.Result;
    }
    // It was valid JSON but not the expected structure, return original text
    return actionText;
  } catch {
    // It wasn't valid JSON, return the original text as is
    return actionText;
  }
};
export const formatLastActionComment = (
  actionText: string | null | undefined
): string | null => {
  if (!actionText) {
    return null; // Return null if no text
  }

  try {
    const parsed = JSON.parse(actionText);
    // Check if it's an object and has the 'Result' property which is a string
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "Comment" in parsed &&
      typeof parsed.Comment === "string"
    ) {
      return parsed.Comment;
    }
    return null;
  } catch {
    // It wasn't valid JSON, return the original text as is
    return actionText;
  }
};
